import { useState, useEffect, Fragment } from 'react';
import { ref, get, update } from 'firebase/database';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { FaLinkedin, FaGithub, FaFacebook, FaInstagram, FaEdit, FaTimes } from 'react-icons/fa';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

interface VCardProfile {
  adresse?: string;
  codePostal?: string;
  email?: string;
  entreprise?: string;
  facebook?: string;
  github?: string;
  instagram?: string;
  isPublic?: boolean;
  linkedin?: string;
  logoUrl?: string;
  nom?: string;
  pays?: string;
  prenom?: string;
  poste?: string;
  telephone?: string;
  ville?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface UserStats {
  userId: string;
  profiles: VCardProfile[];
  totalProfiles: number;
  publicProfiles: number;
}

interface EditProfileState {
  isOpen: boolean;
  userId: string | null;
  profile: VCardProfile | null;
}

interface TimeRange {
  label: string;
  days: number;
}

const TIME_RANGES: TimeRange[] = [
  { label: '7 jours', days: 7 },
  { label: '30 jours', days: 30 },
  { label: '90 jours', days: 90 }
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProfiles: 0,
    totalPublicProfiles: 0
  });
  const [users, setUsers] = useState<UserStats[]>([]);
  const [editProfile, setEditProfile] = useState<EditProfileState>({
    isOpen: false,
    userId: null,
    profile: null
  });
  const [profilesOverTime, setProfilesOverTime] = useState<any[]>([]);
  const [socialStats, setSocialStats] = useState<any[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(TIME_RANGES[0]);
  const [completionStats, setCompletionStats] = useState<any[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Calculer le taux de complétion d'un profil
  const calculateProfileCompletion = (profile: VCardProfile): number => {
    const fields = [
      'nom', 'prenom', 'email', 'telephone', 'entreprise', 'poste',
      'adresse', 'ville', 'pays', 'codePostal', 'linkedin', 'github',
      'facebook', 'instagram', 'logoUrl'
    ];
    const filledFields = fields.filter(field => !!profile[field as keyof VCardProfile]);
    return Math.round((filledFields.length / fields.length) * 100);
  };

  // Grouper les profils par date
  const groupProfilesByDate = (profiles: VCardProfile[], days: number) => {
    const now = new Date();
    const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    const groupedData: { [key: string]: number } = {};

    // Debug
    // console.log('Total profiles:', profiles.length);
    // console.log('Profiles with createdAt:', profiles.filter(p => p.createdAt).length);
    // console.log('Sample dates:', profiles.slice(0, 3).map(p => ({ 
    //   createdAt: p.createdAt,
    //   parsed: p.createdAt ? new Date(p.createdAt) : null
    // })));

    // Initialiser toutes les dates
    for (let i = 0; i < days; i++) {
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
      const dateStr = date.toLocaleDateString();
      groupedData[dateStr] = 0;
    }

    // Compter les profils par date de création
    profiles.forEach(profile => {
      if (profile.createdAt) {
        const creationDate = new Date(profile.createdAt);
        if (!isNaN(creationDate.getTime()) && creationDate >= startDate) {
          const dateStr = creationDate.toLocaleDateString();
          groupedData[dateStr] = (groupedData[dateStr] || 0) + 1;
        }
      }
    });

    // Debug
    // console.log('Grouped data:', groupedData);

    return Object.entries(groupedData)
      .map(([date, count]) => ({ date, profiles: count }))
      .reverse();
  };

  useEffect(() => {
    const fetchData = async () => {
      const dbRef = ref(db, 'vcards');
      const snapshot = await get(dbRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        // console.log('Raw data from Firebase:', data);
        
        // Migrer les profils sans dates
        await migrateProfiles(data);

        const userStats = Object.entries(data).map(([userId, profile]: [string, any]) => ({
          userId,
          profiles: [profile],
          totalProfiles: 1,
          publicProfiles: profile.isPublic ? 1 : 0
        }));

        // Calculer les statistiques globales
        const totalUsers = userStats.length;
        const totalProfiles = userStats.reduce((acc, curr) => acc + curr.totalProfiles, 0);
        const totalPublicProfiles = userStats.reduce((acc, curr) => acc + curr.publicProfiles, 0);

        setStats({
          totalUsers,
          totalProfiles,
          totalPublicProfiles
        });

        // Calculer les profils par date
        const allProfiles = Object.values(data) as VCardProfile[];
        setProfilesOverTime(groupProfilesByDate(allProfiles, selectedTimeRange.days));

        // Calculer les statistiques des réseaux sociaux
        const socialCounts = {
          LinkedIn: 0,
          GitHub: 0,
          Facebook: 0,
          Instagram: 0
        };

        // Calculer les taux de complétion
        const completionGroups = {
          '0-25%': 0,
          '26-50%': 0,
          '51-75%': 0,
          '76-100%': 0
        };

        Object.values(data).forEach((profile: VCardProfile) => {
          // Réseaux sociaux
          if (profile.linkedin) socialCounts.LinkedIn++;
          if (profile.github) socialCounts.GitHub++;
          if (profile.facebook) socialCounts.Facebook++;
          if (profile.instagram) socialCounts.Instagram++;

          // Taux de complétion
          const completion = calculateProfileCompletion(profile);
          if (completion <= 25) completionGroups['0-25%']++;
          else if (completion <= 50) completionGroups['26-50%']++;
          else if (completion <= 75) completionGroups['51-75%']++;
          else completionGroups['76-100%']++;
        });

        setSocialStats(Object.entries(socialCounts).map(([name, value]) => ({
          name,
          value
        })));

        setCompletionStats(Object.entries(completionGroups).map(([range, count]) => ({
          range,
          count
        })));

        setUsers(userStats);
      }
    };

    fetchData();
  }, [selectedTimeRange]);

  // Migrer les profils sans dates
  const migrateProfiles = async (profiles: { [key: string]: VCardProfile }) => {
    try {
      const now = new Date().toISOString();
      const updates: { [key: string]: any } = {};

      Object.entries(profiles).forEach(([userId, profile]) => {
        if (!profile.createdAt) {
          updates[`vcards/${userId}`] = {
            ...profile,
            createdAt: now,
            updatedAt: now
          };
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
        // console.log(`Migrated ${Object.keys(updates).length} profiles`);
        // Recharger les données
        fetchData();
      }
    } catch (error) {
      console.error('Error migrating profiles:', error);
    }
  };

  // Couleurs pour les graphiques
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const handleEdit = (userId: string, profile: VCardProfile) => {
    setEditProfile({
      isOpen: true,
      userId,
      profile: { ...profile }
    });
  };

  const handleClose = () => {
    setEditProfile({
      isOpen: false,
      userId: null,
      profile: null
    });
  };

  const handleSave = async () => {
    if (!editProfile.userId || !editProfile.profile) return;

    try {
      const profileRef = ref(db, `vcards/${editProfile.userId}`);
      await update(profileRef, editProfile.profile);
      
      // Rafraîchir les données
      const dbRef = ref(db, 'vcards');
      const snapshot = await get(dbRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const userStats = Object.entries(data).map(([userId, profile]: [string, any]) => ({
          userId,
          profiles: [profile],
          totalProfiles: 1,
          publicProfiles: profile.isPublic ? 1 : 0
        }));
        
        setUsers(userStats);
      }
      
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const handleChange = (field: keyof VCardProfile, value: string | boolean) => {
    if (!editProfile.profile) return;
    
    setEditProfile(prev => ({
      ...prev,
      profile: {
        ...prev.profile!,
        [field]: value
      }
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Graphiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Évolution des profils */}
        <div className="bg-white p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Évolution des profils</h3>
            <select
              className="px-3 py-1 border rounded-md text-sm"
              value={selectedTimeRange.days}
              onChange={(e) => setSelectedTimeRange(
                TIME_RANGES.find(range => range.days === Number(e.target.value)) || TIME_RANGES[0]
              )}
            >
              {TIME_RANGES.map(range => (
                <option key={range.days} value={range.days}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={profilesOverTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="profiles"
                name="Nouveaux profils"
                stroke="#0088FE"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Répartition public/privé */}
        <div className="bg-white p-4 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Profils publics vs privés</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Public', value: stats.totalPublicProfiles },
                  { name: 'Privé', value: stats.totalProfiles - stats.totalPublicProfiles }
                ]}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
                animationBegin={0}
                animationDuration={1500}
              >
                {[0, 1].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Taux de complétion */}
        <div className="bg-white p-4 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Taux de complétion des profils</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={completionStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="count"
                name="Nombre de profils"
                fill="#0088FE"
                animationBegin={0}
                animationDuration={1500}
              >
                {completionStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Statistiques supplémentaires */}
        <div className="bg-white p-4 rounded-lg shadow-lg md:col-span-3">
          <h3 className="text-lg font-semibold mb-4">Réseaux sociaux utilisés</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={socialStats} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={80} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="value"
                name="Nombre d'utilisateurs"
                fill="#0088FE"
                animationBegin={0}
                animationDuration={1500}
              >
                {socialStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Contenu existant */}
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold mb-6">Dashboard Administrateur</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-indigo-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-indigo-900">Total Utilisateurs</h3>
              <p className="text-3xl font-bold text-indigo-600">{stats.totalUsers}</p>
            </div>
            
            <div className="bg-indigo-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-indigo-900">Total Profils</h3>
              <p className="text-3xl font-bold text-indigo-600">{stats.totalProfiles}</p>
            </div>
            
            <div className="bg-indigo-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-indigo-900">Profils Publics</h3>
              <p className="text-3xl font-bold text-indigo-600">{stats.totalPublicProfiles}</p>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Liste des Utilisateurs</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entreprise
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Réseaux sociaux
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userStat) => {
                    const profile = userStat.profiles[0];
                    return (
                      <tr key={userStat.userId}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {profile.logoUrl && (
                              <img 
                                src={profile.logoUrl} 
                                alt="Logo" 
                                className="h-10 w-10 rounded-full mr-3"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {profile.nom} {profile.prenom}
                              </div>
                              <div className="text-sm text-gray-500">
                                {profile.pays}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {profile.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {profile.telephone}
                          </div>
                          <div className="text-sm text-gray-500">
                            {profile.adresse}, {profile.codePostal}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {profile.entreprise}
                          </div>
                          <div className="text-sm text-gray-500">
                            {profile.poste}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-4">
                            {profile.linkedin && (
                              <a 
                                href={profile.linkedin} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[#0077B5] hover:text-[#0077B5]/80"
                                title="LinkedIn"
                              >
                                <FaLinkedin size={24} />
                              </a>
                            )}
                            {profile.github && (
                              <a 
                                href={profile.github} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-gray-800 hover:text-gray-600"
                                title="GitHub"
                              >
                                <FaGithub size={24} />
                              </a>
                            )}
                            {profile.facebook && (
                              <a 
                                href={profile.facebook} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[#1877F2] hover:text-[#1877F2]/80"
                                title="Facebook"
                              >
                                <FaFacebook size={24} />
                              </a>
                            )}
                            {profile.instagram && (
                              <a 
                                href={profile.instagram} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[#E4405F] hover:text-[#E4405F]/80"
                                title="Instagram"
                              >
                                <FaInstagram size={24} />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {profile.isPublic ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Public
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              Privé
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleEdit(userStat.userId, profile)}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                            title="Modifier le profil"
                          >
                            <FaEdit size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <Transition.Root show={editProfile.isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-hidden z-50" onClose={handleClose}>
              <div className="absolute inset-0 overflow-hidden">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                  <Transition.Child
                    as={Fragment}
                    enter="transform transition ease-in-out duration-500"
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave="transform transition ease-in-out duration-500"
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                  >
                    <Dialog.Panel className="w-screen max-w-md">
                      <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto">
                        {/* Header */}
                        <div className="px-6 py-6 bg-gradient-to-r from-blue-600 to-blue-700">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              Modifier le profil
                            </Dialog.Title>
                            <button
                              type="button"
                              className="rounded-md text-white hover:text-gray-200 focus:outline-none"
                              onClick={handleClose}
                            >
                              <FaTimes className="h-6 w-6" />
                            </button>
                          </div>
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 px-6 py-6 space-y-6">
                          {/* Informations personnelles */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                              Informations personnelles
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Prénom
                                </label>
                                <input
                                  type="text"
                                  value={editProfile.profile?.prenom || ''}
                                  onChange={(e) => handleChange('prenom', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Nom
                                </label>
                                <input
                                  type="text"
                                  value={editProfile.profile?.nom || ''}
                                  onChange={(e) => handleChange('nom', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                              </label>
                              <input
                                type="email"
                                value={editProfile.profile?.email || ''}
                                onChange={(e) => handleChange('email', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Téléphone
                              </label>
                              <input
                                type="tel"
                                value={editProfile.profile?.telephone || ''}
                                onChange={(e) => handleChange('telephone', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          {/* Informations professionnelles */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                              Informations professionnelles
                            </h3>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Entreprise
                              </label>
                              <input
                                type="text"
                                value={editProfile.profile?.entreprise || ''}
                                onChange={(e) => handleChange('entreprise', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Poste
                              </label>
                              <input
                                type="text"
                                value={editProfile.profile?.poste || ''}
                                onChange={(e) => handleChange('poste', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          {/* Réseaux sociaux */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                              Réseaux sociaux
                            </h3>
                            <div className="space-y-3">
                              <div className="flex items-center space-x-2">
                                <FaLinkedin className="text-blue-600 w-5 h-5" />
                                <input
                                  type="text"
                                  value={editProfile.profile?.linkedin || ''}
                                  onChange={(e) => handleChange('linkedin', e.target.value)}
                                  placeholder="URL LinkedIn"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <FaGithub className="text-gray-800 w-5 h-5" />
                                <input
                                  type="text"
                                  value={editProfile.profile?.github || ''}
                                  onChange={(e) => handleChange('github', e.target.value)}
                                  placeholder="URL GitHub"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <FaFacebook className="text-blue-700 w-5 h-5" />
                                <input
                                  type="text"
                                  value={editProfile.profile?.facebook || ''}
                                  onChange={(e) => handleChange('facebook', e.target.value)}
                                  placeholder="URL Facebook"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <FaInstagram className="text-pink-600 w-5 h-5" />
                                <input
                                  type="text"
                                  value={editProfile.profile?.instagram || ''}
                                  onChange={(e) => handleChange('instagram', e.target.value)}
                                  placeholder="URL Instagram"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Visibilité du profil */}
                          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-900">Profil public</span>
                            <button
                              type="button"
                              onClick={() => handleChange('isPublic', !editProfile.profile?.isPublic)}
                              className={`${
                                editProfile.profile?.isPublic ? 'bg-blue-600' : 'bg-gray-200'
                              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none`}
                            >
                              <span
                                className={`${
                                  editProfile.profile?.isPublic ? 'translate-x-5' : 'translate-x-0'
                                } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-t">
                          <div className="flex justify-end space-x-3">
                            <button
                              type="button"
                              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              onClick={handleClose}
                            >
                              Annuler
                            </button>
                            <button
                              type="button"
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              onClick={handleSave}
                            >
                              Enregistrer
                            </button>
                          </div>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>

          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Votre compte admin</h2>
            <p className="text-gray-600">Connecté en tant que : {user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
